import React, { useEffect, useState, useMemo } from 'react';
import { Container } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { emptyPrev, loadProductsAsync, loadQueryProductsAsync, productSorting } from '../../../redux/feathers/productsSlice';
import ProductCard from '../../HomePage/ProductCard/ProductCard';
import LoadingSpinner from '../../SharedComponents/LoadingSpinner/LoadingSpinner';
import styles from './SingleCategory.module.css';

const SingleCategory = () => {
  const { searchString } = useParams();
  const dispatch = useDispatch();
  const products = useSelector((state) => state.products);
  const categories = useSelector((state) => state.products.categories);
  const pagination = useSelector((state) => state.products.pagination);
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 12;

  // Get category IDs to filter (includes sub-categories if parent is selected)
  const categoryIds = useMemo(() => {
    if (searchString === 'all') return [];

    const ids = [searchString];

    // Find sub-categories of the selected category
    categories.forEach((cat) => {
      if (cat.parentId === searchString) {
        ids.push(cat.id);
      }
    });

    return ids;
  }, [searchString, categories]);

  // Reset to page 0 when category changes
  useEffect(() => {
    setCurrentPage(0);
  }, [searchString]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
    });
    dispatch(emptyPrev());

    // If "all" is selected, load all products, otherwise filter by category IDs
    if (searchString === 'all') {
      dispatch(loadProductsAsync({ page: currentPage, size: pageSize }));
    } else {
      // Pass array of category IDs (includes parent + sub-categories)
      dispatch(loadQueryProductsAsync({ categoryIds, page: currentPage, size: pageSize }));
    }
  }, [dispatch, searchString, currentPage, categoryIds]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(0, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(pagination.totalPages - 1, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(0, endPage - maxVisiblePages + 1);
    }

    // Previous button
    pages.push(
      <button
        key="prev"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 0}
        className={styles.pagination__btn}
      >
        Previous
      </button>
    );

    // First page
    if (startPage > 0) {
      pages.push(
        <button
          key={0}
          onClick={() => handlePageChange(0)}
          className={`${styles.pagination__number} ${currentPage === 0 ? styles.active : ''}`}
        >
          1
        </button>
      );
      if (startPage > 1) {
        pages.push(<span key="dots1" className={styles.pagination__dots}>...</span>);
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`${styles.pagination__number} ${currentPage === i ? styles.active : ''}`}
        >
          {i + 1}
        </button>
      );
    }

    // Last page
    if (endPage < pagination.totalPages - 1) {
      if (endPage < pagination.totalPages - 2) {
        pages.push(<span key="dots2" className={styles.pagination__dots}>...</span>);
      }
      pages.push(
        <button
          key={pagination.totalPages - 1}
          onClick={() => handlePageChange(pagination.totalPages - 1)}
          className={`${styles.pagination__number} ${currentPage === pagination.totalPages - 1 ? styles.active : ''}`}
        >
          {pagination.totalPages}
        </button>
      );
    }

    // Next button
    pages.push(
      <button
        key="next"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === pagination.totalPages - 1}
        className={styles.pagination__btn}
      >
        Next
      </button>
    );

    return <div className={styles.pagination}>{pages}</div>;
  };

  return (
    <Container>
      {products.status === 'Pending' ? (
        <div className='mt-5 pt-5'>
          <LoadingSpinner />
        </div>
      ) : (
        <>
          <div className='d-flex justify-content-between mb-4'>
            <h6>
              Total <strong>{pagination.totalElements || 0}</strong> items Found
            </h6>
            <form onSubmit={(e) => e.preventDefault()}>
              <select name='price' onChange={(e) => dispatch(productSorting(e.target.value))}>
                <option value='high'>High to Low</option>
                <option value='low'>Low to High</option>
              </select>
            </form>
          </div>

          <div className={styles.category__container}>
            {(searchString === 'all' ? products.productsState : products.queryProductsState).map((product) => {
              // Support both id (new) and _id (old)
              const productKey = product.id || product._id;
              return <ProductCard product={product} key={productKey} />;
            })}
          </div>

          {renderPagination()}
        </>
      )}
    </Container>
  );
};

export default SingleCategory;
